import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { ResponsiveImage } from '../../components';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../../components/ui/carousel';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';

const MAIN_VARIANTS = ['scaled-large', 'scaled-medium', 'scaled-small', 'scaled-xlarge'];

const CustomListingImageGallery = ({ listing, variantPrefix }) => {
  const [mainApi, setMainApi] = useState(null);
  const [thumbApi, setThumbApi] = useState(null);
  const [lightboxApi, setLightboxApi] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = listing?.images || [];
  const title = listing?.attributes?.title || '';
  const thumbVariants = [`${variantPrefix}-2x`, variantPrefix];

  const onSelect = useCallback(() => {
    if (!mainApi) return;
    const idx = mainApi.selectedScrollSnap();
    setSelectedIndex(idx);
    if (thumbApi) thumbApi.scrollTo(idx);
  }, [mainApi, thumbApi]);

  useEffect(() => {
    if (!mainApi) return undefined;
    onSelect();
    mainApi.on('select', onSelect);
    mainApi.on('reInit', onSelect);
    return () => {
      mainApi.off('select', onSelect);
    };
  }, [mainApi, onSelect]);

  useEffect(() => {
    if (lightboxOpen && lightboxApi) {
      lightboxApi.scrollTo(selectedIndex, true);
    }
  }, [lightboxOpen, lightboxApi, selectedIndex]);

  const handleThumbClick = idx => {
    if (mainApi) mainApi.scrollTo(idx);
  };

  if (images.length === 0) {
    return (
      <div className="bg-background border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="aspect-[16/10] max-h-[460px] bg-muted" />
      </div>
    );
  }

  return (
    <>
      <div className="bg-background border border-border rounded-xl overflow-hidden shadow-sm">
        <Carousel setApi={setMainApi} opts={{ loop: images.length > 1 }} className="relative">
          <CarouselContent className="ml-0">
            {images.map((img, idx) => (
              <CarouselItem key={img?.id?.uuid || idx} className="pl-0">
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  className="relative w-full h-[460px] max-h-[60vh] bg-muted flex items-center justify-center cursor-zoom-in"
                  aria-label="Open image fullscreen"
                >
                  <ResponsiveImage
                    rootClassName="max-w-full max-h-full w-auto h-auto object-contain"
                    image={img}
                    alt={`${title} ${idx + 1}`}
                    variants={MAIN_VARIANTS}
                    sizes="(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px"
                  />
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>
          {images.length > 1 && (
            <>
              <CarouselPrevious className="left-3" />
              <CarouselNext className="right-3" />
              <div className="absolute bottom-3 right-3 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white">
                {selectedIndex + 1} / {images.length}
              </div>
            </>
          )}
        </Carousel>

        {images.length > 1 && (
          <Carousel
            setApi={setThumbApi}
            opts={{ containScroll: 'keepSnaps', dragFree: true }}
            className="border-t border-border bg-background"
          >
            <CarouselContent className="-ml-2 p-2">
              {images.map((img, idx) => (
                <CarouselItem key={img?.id?.uuid || idx} className="pl-2 basis-auto">
                  <button
                    type="button"
                    onClick={() => handleThumbClick(idx)}
                    className={classNames(
                      'relative w-20 h-20 rounded-md overflow-hidden flex-shrink-0 border-2 transition-all',
                      selectedIndex === idx
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-transparent hover:border-primary/50'
                    )}
                  >
                    <ResponsiveImage
                      rootClassName="absolute inset-0 w-full h-full object-cover"
                      image={img}
                      alt={`${title} ${idx + 1}`}
                      variants={thumbVariants}
                      sizes="80px"
                    />
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        )}
      </div>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="bg-black/95 border-0 p-0 gap-0 overflow-hidden sm:max-w-none sm:rounded-none sm:max-h-none sm:inset-0 sm:translate-x-0 sm:translate-y-0 sm:left-0 sm:top-0">
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <Carousel
            setApi={setLightboxApi}
            opts={{ loop: images.length > 1, startIndex: selectedIndex }}
            className="h-screen w-screen flex items-center justify-center"
          >
            <CarouselContent className="ml-0 h-screen">
              {images.map((img, idx) => (
                <CarouselItem
                  key={img?.id?.uuid || idx}
                  className="pl-0 flex items-center justify-center"
                >
                  <ResponsiveImage
                    rootClassName="max-w-[95vw] max-h-[95vh] w-auto h-auto object-contain"
                    image={img}
                    alt={`${title} ${idx + 1}`}
                    variants={MAIN_VARIANTS}
                    sizes="100vw"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            {images.length > 1 && (
              <>
                <CarouselPrevious className="left-4 h-12 w-12 bg-white/10 border-white/20 text-white hover:bg-white/20" />
                <CarouselNext className="right-4 h-12 w-12 bg-white/10 border-white/20 text-white hover:bg-white/20" />
              </>
            )}
          </Carousel>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomListingImageGallery;
